"use client";

import React, { useEffect } from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { Wifi, ArrowRight, Star } from "lucide-react";

type Destination = { code: string; name: string; price?: string };

const BADGES = ["Best seller", "Top rated", "Popular", "Great value"];
const BADGE_KEYS = ["bestSeller", "topRated", "popular", "greatValue"];

/** Market-test: curated price-from showcase vs raw grid + regional/global
 *  upsell (market-test-plan.md §3 `pricing_premium_regional_v1`; research/07
 *  CF4 — curated tiers + corridor upsell, no discount lead). */
const EXPERIMENT_ID = "pricing_premium_regional_v1";

/**
 * SkyLane-native popular destinations showcase — crisp solid white cards with a
 * flat bg-primary/10 flag panel, name, 4G/5G coverage, price-from and a solid
 * electric-blue "View plans" CTA. No glass, no gradients. Presentational only;
 * the orchestrator wires `destinations` in from build-time pricing data.
 */
export default function PlansShowcase({ destinations }: { destinations: { code: string; name: string; price?: string }[] }) {
  const { t } = useTranslations();

  useEffect(() => {
    try {
      track({
        event_name: "plans_showcase_viewed",
        event_id: `plans_showcase_viewed:${EXPERIMENT_ID}`,
        experiment_id: EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  const onPlanClick = (code: string) => {
    try {
      track({
        event_name: "destination_card_clicked",
        event_id: `destination_card_clicked:${EXPERIMENT_ID}:${code}`,
        experiment_id: EXPERIMENT_ID,
      });
    } catch {
      /* analytics is best-effort */
    }
  };

  return (
    <section className="container mx-auto px-4 py-12">
      <Reveal as="div" y={16} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
          {t("sky.plans.title", "Popular route-ready destinations")}
        </h2>
        <Link
          href="/destinations/"
          className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          {t("sky.plans.viewAll", "View all destinations")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </Reveal>

      <Stagger as="div" staggerChildren={0.05} amount={0.15} className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {destinations.map((destination: Destination, i: number) => {
          const code = destination.code.toLowerCase();
          const badge = t(`sky.plans.badge.${BADGE_KEYS[i % BADGE_KEYS.length]}`, BADGES[i % BADGES.length]);
          return (
            <StaggerItem
              key={destination.code}
              as="div"
              y={12}
              className="group rounded-2xl border border-border bg-card overflow-hidden shadow-sm transition-all hover:-translate-y-1 hover:shadow-md hover:border-primary/40"
            >
              <div className="relative h-28 bg-primary/10 flex items-center justify-center overflow-hidden">
                <img
                  src={`https://flagcdn.com/${code}.svg`}
                  alt=""
                  role="presentation"
                  width={56}
                  height={40}
                  className="h-10 w-14 rounded shadow transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
                <span className="absolute top-2 left-2 rounded-full bg-card text-foreground text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 inline-flex items-center gap-1 shadow-sm">
                  <Star className="w-3 h-3 text-success" />
                  {badge}
                </span>
              </div>
              <div className="p-4">
                <div className="font-semibold text-foreground">{destination.name}</div>
                <div className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Wifi className="w-3.5 h-3.5 text-primary" />
                  {t("sky.plans.coverage", "4G/5G coverage")}
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="text-sm font-bold text-foreground">
                    {destination.price
                      ? t("sky.plans.fromPrice", "from {price}").replace("{price}", destination.price)
                      : destination.code.toUpperCase()}
                  </span>
                </div>
                <Link
                  href={`/destinations/${code}/`}
                  onClick={() => onPlanClick(code)}
                  className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground font-semibold h-11 hover:bg-primary/90 transition-all active:scale-[0.98]"
                >
                  {t("sky.plans.view", "View plans")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </section>
  );
}
